import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Input, Switch, Drawer, message, Select, DatePicker, Button, Form, Tooltip } from 'antd';
import {
	ExportOutlined,
	RedoOutlined,
	PlusOutlined,
	EyeOutlined,
	CloseOutlined,
	CloseCircleOutlined,
} from '@ant-design/icons';
// eslint-disable-next-line import/no-extraneous-dependencies
import dayjs from 'dayjs';
import styled from 'styled-components';
import { ButtonWrapper } from 'components';
import { FilterAtnButton } from 'utils/commonStyled';
import { AntdTable } from 'components/custom-table/Style';
import { Box, Flex, Text } from 'atoms';
import { FilterTag } from 'components/common';
import {
	searchVehicleTypesV2,
	exportVehicleTypesCSVV2,
	deleteVehicleTypeV2,
	updateVehicleTypeV2,
} from 'store/vehicleSliceV2';
import { usePersistedState } from 'hooks';

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const LtSearchBar = styled(Search)`
	border: none;
`;

const StyledFilterButton = styled(FilterAtnButton)`
	margin-left: 16px;
`;

const ViewButton = styled(ButtonWrapper)`
	padding: 4px;
	min-width: 32px;
	min-height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const FilterDisplayBox = styled(Box)`
	flex: 1;
	min-width: 200px;
	height: 37px;
	padding: 10px 10px 10px 12px;
	background: #ffffff;
	border: 1px solid #c6c6c8;
	border-radius: 2px;
	display: flex;
	align-items: center;
	overflow-x: auto;
	white-space: nowrap;
	gap: 8px;
	&::-webkit-scrollbar {
		height: 4px;
	}
	&::-webkit-scrollbar-thumb {
		background: #c6c6c8;
		border-radius: 2px;
	}
`;

const FilterForm = styled(Form)`
	.ant-form-item {
		margin-bottom: 20px;
	}

	.ant-form-item-label > label {
		font-weight: 500;
		color: #374151;
	}
`;

const FilterHeader = styled(Flex)`
	justify-content: space-between;
	align-items: center;
	padding: 16px 0;
`;

const FilterTitleText = styled(Text)`
	font-size: 20px;
	font-weight: 600;
	color: #333;
`;

const FilterCloseButton = styled(CloseCircleOutlined)`
	font-size: 20px;
	color: #999;
	cursor: pointer;

	&:hover {
		color: #666;
	}
`;

const FilterButtonContainer = styled(Flex)`
	margin-top: 24px;
	gap: 12px;
`;

const ResetButton = styled(Button)`
	width: 100%;
	height: 40px;
	padding: 0 24px;
	font-weight: 500;
	border: 1px solid #d1d5db;
	color: #374151;
	background: white;

	&:hover {
		border-color: #9ca3af;
		color: #1f2937;
		background: white;
	}
`;

const getTableColumns = (navigate, handleStatusChange) => [
	{
		title: 'Vehicle Type Name',
		dataIndex: 'vehicle_type_name',
		key: 'vehicle_type_name',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Category',
		dataIndex: 'vehicle_category',
		key: 'vehicle_category',
		render: (text) => <Text>{text ? text.charAt(0).toUpperCase() + text.slice(1) : '-'}</Text>,
	},
	{
		title: 'Capacity (Ton)',
		dataIndex: 'capacity_tonnes',
		key: 'capacity_tonnes',
		sorter: true,
		render: (text) => <Text>{text ? parseFloat(text).toFixed(2) : '-'}</Text>,
	},
	{
		title: 'Length (Feet)',
		dataIndex: 'length_feet',
		key: 'length_feet',
		render: (text) => <Text>{text ? parseFloat(text).toFixed(2) : '-'}</Text>,
	},
	{
		title: 'Usage Count',
		dataIndex: 'usage_count',
		key: 'usage_count',
		render: (count) => <Text>{count || 0}</Text>,
	},
	{
		title: 'Created at',
		dataIndex: 'created_at',
		key: 'created_at',
		render: (text) => (
			<Text>
				{text
					? new Date(text).toLocaleDateString('en-IN', {
							day: '2-digit',
							month: 'short',
							year: 'numeric',
					  })
					: '-'}
			</Text>
		),
	},
	{
		title: 'Created by',
		dataIndex: 'created_by_name',
		key: 'created_by_name',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Status',
		dataIndex: 'is_active',
		key: 'is_active',
		render: (status, record) => (
			<Switch
				checked={status}
				checkedChildren="Active"
				unCheckedChildren="Inactive"
				onChange={(checked) => handleStatusChange(record, checked)}
			/>
		),
	},
	{
		title: 'Actions',
		dataIndex: 'id',
		key: 'actions',
		render: (id) => (
			<ViewButton onClick={() => navigate(`/vehicle-type/${id}`)}>
				<EyeOutlined style={{ fontSize: 18, color: '#1890ff' }} />
			</ViewButton>
		),
	},
];

export default function VehicleTypeListPageV2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [filterForm] = Form.useForm();
	const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
	const [searchValue, setSearchValue] = usePersistedState('vehicleTypeList:search', '');
	const [appliedFilters, setAppliedFilters] = usePersistedState('vehicleTypeList:filters', {});
	const [vehicleTypes, setVehicleTypes] = useState([]);
	const [loading, setLoading] = useState(false);
	const [pagination, setPagination] = usePersistedState('vehicleTypeList:pagination', {
		current: 1,
		pageSize: 20,
		total: 0,
	});
	const filterChangeTimeoutRef = useRef(null);

	const fetchVehicleTypes = async (page = 1, filters = null, pageSize = pagination.pageSize) => {
		setLoading(true);
		try {
			const activeFilters = filters !== null ? filters : appliedFilters;

			const response = await dispatch(
				searchVehicleTypesV2({
					filter_by: activeFilters,
					pagination: {
						page,
						per_page: pageSize,
					},
					sort_by: {
						field: 'vehicle_type_name',
						order: 'asc',
					},
				})
			);

			if (response) {
				setVehicleTypes(response.rows || []);
				setPagination((prev) => ({
					...prev,
					current: page,
					pageSize,
					total: response.count || 0,
				}));
			}
		} catch (error) {
			console.error('Error fetching vehicle types:', error);
			message.error('Failed to fetch vehicle types');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchVehicleTypes(
			pagination.current,
			{ ...appliedFilters, ...(searchValue ? { vehicle_type_name: searchValue } : {}) },
			pagination.pageSize
		);
		return () => {
			if (filterChangeTimeoutRef.current) {
				clearTimeout(filterChangeTimeoutRef.current);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Populate filter form when drawer opens
	useEffect(() => {
		if (filterDrawerOpen) {
			const formValues = {};

			// Status filter
			if (appliedFilters.is_active !== undefined) {
				formValues.status = appliedFilters.is_active ? 'true' : 'false';
			}

			// Category filter
			if (appliedFilters.vehicle_category) {
				formValues.category = appliedFilters.vehicle_category;
			}

			// Date range filter
			if (appliedFilters.created_at_from && appliedFilters.created_at_to) {
				formValues.dateRange = [
					dayjs(appliedFilters.created_at_from),
					dayjs(appliedFilters.created_at_to),
				];
			}

			filterForm.setFieldsValue(formValues);
		}
	}, [filterDrawerOpen, appliedFilters, filterForm]);

	const handleSearch = (value) => {
		const trimmedValue = value ? value.trim() : '';
		setSearchValue(trimmedValue);
		const filters = {
			...appliedFilters,
			...(trimmedValue ? { vehicle_type_name: trimmedValue } : {}),
		};
		fetchVehicleTypes(1, filters);
	};

	const handleExport = async () => {
		try {
			await dispatch(
				exportVehicleTypesCSVV2({
					filter_by: {
						...appliedFilters,
						...(searchValue ? { vehicle_type_name: searchValue } : {}),
					},
					format: 'csv',
				})
			);
			message.success('Export initiated successfully');
		} catch (error) {
			console.error('Export error:', error);
			message.error('Failed to export vehicle types');
		}
	};

	const handleSync = async () => {
		try {
			message.loading({ content: 'Syncing vehicle types...', key: 'sync' });

			// Refetch vehicle types with current filters (same as vehicle list)
			await fetchVehicleTypes(
				1,
				{
					...appliedFilters,
					...(searchValue ? { vehicle_type_name: searchValue } : {}),
				},
				pagination.pageSize
			);

			message.success({
				content: 'Vehicle types synced successfully',
				key: 'sync',
				duration: 2,
			});
		} catch (error) {
			console.error('Sync error:', error);
			message.error({ content: 'Failed to sync vehicle types', key: 'sync', duration: 3 });
		}
	};

	const handleCreate = () => {
		navigate('/vehicle-type/add');
	};

	const handleStatusChange = async (record, checked) => {
		try {
			if (!checked) {
				// Deactivating - use delete action (soft delete)
				const response = await dispatch(deleteVehicleTypeV2({ id: record.id }));
				if (response?.status === 'success') {
					// Refresh the list
					fetchVehicleTypes(pagination.current, {
						...appliedFilters,
						...(searchValue ? { vehicle_type_name: searchValue } : {}),
					});
				}
			} else {
				// Activating - use update action
				const response = await dispatch(
					updateVehicleTypeV2({
						req_data: {
							id: record.id,
							is_active: true,
						},
						setErrors: () => {},
					})
				);
				if (response?.status === 'success') {
					// Refresh the list
					fetchVehicleTypes(pagination.current, {
						...appliedFilters,
						...(searchValue ? { vehicle_type_name: searchValue } : {}),
					});
				}
			}
		} catch (error) {
			console.error('Status change error:', error);
			message.error('Failed to update vehicle type status');
		}
	};

	// Helper function to build filters from form values
	const buildFiltersFromFormValues = (values) => {
		const filters = {};

		// Status filter
		if (values.status !== undefined && values.status !== null && values.status !== '') {
			filters.is_active = values.status === 'true' || values.status === true;
		}

		// Category filter
		if (values.category) {
			filters.vehicle_category = values.category;
		}

		// Date range filter
		if (values.dateRange && values.dateRange.length === 2) {
			const startDate = values.dateRange[0];
			const endDate = values.dateRange[1];
			// DatePicker returns dayjs objects, format to ISO string
			if (startDate && endDate) {
				filters.created_at_from = startDate.startOf('day').toISOString();
				filters.created_at_to = endDate.endOf('day').toISOString();
			}
		}

		return filters;
	};

	// Auto-apply filters when form values change
	const handleFilterChange = () => {
		if (filterChangeTimeoutRef.current) {
			clearTimeout(filterChangeTimeoutRef.current);
		}

		filterChangeTimeoutRef.current = setTimeout(() => {
			const currentValues = filterForm.getFieldsValue();
			const filters = buildFiltersFromFormValues(currentValues);
			setAppliedFilters(filters);
			fetchVehicleTypes(1, {
				...(searchValue ? { vehicle_type_name: searchValue } : {}),
				...filters,
			});
		}, 150);
	};

	const handleResetFilters = () => {
		if (filterChangeTimeoutRef.current) {
			clearTimeout(filterChangeTimeoutRef.current);
		}
		filterForm.resetFields();
		setAppliedFilters({});
		fetchVehicleTypes(1, searchValue ? { vehicle_type_name: searchValue } : {});
	};

	// Remove individual filter
	const handleRemoveFilter = (filterKey) => {
		const newFilters = { ...appliedFilters };

		if (filterKey === 'dateRange') {
			delete newFilters.created_at_from;
			delete newFilters.created_at_to;
			filterForm.setFieldsValue({ dateRange: null });
		} else if (filterKey === 'is_active') {
			delete newFilters.is_active;
			filterForm.setFieldsValue({ status: undefined });
		} else if (filterKey === 'vehicle_category') {
			delete newFilters.vehicle_category;
			filterForm.setFieldsValue({ category: undefined });
		}

		setAppliedFilters(newFilters);
		fetchVehicleTypes(1, {
			...(searchValue ? { vehicle_type_name: searchValue } : {}),
			...newFilters,
		});
	};

	// Clear all filters
	const handleClearAllFilters = () => handleResetFilters();

	// Get filter tags for display
	const getAppliedFiltersDisplay = () =>
		[
			appliedFilters.is_active !== undefined ? { key: 'is_active', label: 'Status' } : null,
			appliedFilters.vehicle_category ? { key: 'vehicle_category', label: 'Category' } : null,
			appliedFilters.created_at_from && appliedFilters.created_at_to
				? { key: 'dateRange', label: 'Created Date' }
				: null,
		].filter(Boolean);

	const activeFiltersCount = getAppliedFiltersDisplay().length;

	const handleTableChange = (paginationConfig, filters, sorter) => {
		const sortBy = sorter?.field
			? {
					field: sorter.field,
					order: sorter.order === 'ascend' ? 'asc' : 'desc',
			  }
			: {
					field: 'vehicle_type_name',
					order: 'asc',
			  };

		setLoading(true);
		try {
			dispatch(
				searchVehicleTypesV2({
					filter_by: {
						...appliedFilters,
						...(searchValue ? { vehicle_type_name: searchValue } : {}),
					},
					pagination: {
						page: paginationConfig.current,
						per_page: paginationConfig.pageSize,
					},
					sort_by: sortBy,
				})
			).then((response) => {
				if (response) {
					setVehicleTypes(response.rows || []);
					setPagination({
						current: paginationConfig.current,
						pageSize: paginationConfig.pageSize,
						total: response.count || 0,
					});
				}
			});
		} catch (error) {
			console.error('Error fetching vehicle types:', error);
			message.error('Failed to fetch vehicle types');
		} finally {
			setLoading(false);
		}
	};

	return (
		<Box>
			<Flex justifyContent="space-between" alignItems="center" mt={2} mb={2}>
				<Flex alignItems="center" gap="10px" flex="1">
					<Box flex="0 0 20%">
						<LtSearchBar
							placeholder="Search"
							enterButton="Search"
							allowClear
							onSearch={handleSearch}
							value={searchValue}
							onChange={(e) => setSearchValue(e.target.value)}
						/>
					</Box>
					<StyledFilterButton onClick={() => setFilterDrawerOpen(true)}>
						FILTERS {activeFiltersCount > 0 && `(${activeFiltersCount})`}
					</StyledFilterButton>
					<FilterDisplayBox>
						{getAppliedFiltersDisplay().map((filter) => (
							<FilterTag
								key={filter.key}
								onClose={() => handleRemoveFilter(filter.key)}
							>
								{filter.label}
							</FilterTag>
						))}
						{activeFiltersCount > 0 && (
							<Tooltip title="Clear all filters">
								<CloseOutlined
									style={{
										color: '#ff4d4f',
										cursor: 'pointer',
										marginLeft: 'auto',
										marginRight: '4px',
									}}
									onClick={handleClearAllFilters}
								/>
							</Tooltip>
						)}
					</FilterDisplayBox>
				</Flex>
				<Flex gap="10px">
					<ButtonWrapper onClick={handleExport} icons={<ExportOutlined />}>
						EXPORT
					</ButtonWrapper>
					<ButtonWrapper onClick={handleSync} icons={<RedoOutlined />}>
						SYNC
					</ButtonWrapper>
					<ButtonWrapper onClick={handleCreate} icons={<PlusOutlined />}>
						CREATE
					</ButtonWrapper>
				</Flex>
			</Flex>

			<Box mt={3}>
				<AntdTable
					rowKey="id"
					columns={getTableColumns(navigate, handleStatusChange)}
					dataSource={vehicleTypes}
					loading={loading}
					pagination={{
						current: pagination.current,
						pageSize: pagination.pageSize,
						total: pagination.total,
						showSizeChanger: true,
						showTotal: (total) => `Total ${total} vehicle types`,
					}}
					onChange={handleTableChange}
				/>
			</Box>

			<Drawer
				className="userList-drawer"
				placement="right"
				closable={false}
				onClose={() => setFilterDrawerOpen(false)}
				open={filterDrawerOpen}
				title={null}
				headerStyle={{ display: 'none' }}
			>
				<Box>
					<FilterHeader>
						<FilterTitleText>Filters</FilterTitleText>
						<FilterCloseButton onClick={() => setFilterDrawerOpen(false)} />
					</FilterHeader>

					<FilterForm
						form={filterForm}
						layout="vertical"
						onValuesChange={handleFilterChange}
					>
						<Form.Item label="Status" name="status">
							<Select placeholder="Select status" allowClear size="large">
								<Option value="true">Active</Option>
								<Option value="false">Inactive</Option>
							</Select>
						</Form.Item>

						<Form.Item label="Category" name="category">
							<Select placeholder="Select category" allowClear size="large">
								<Option value="light">Light</Option>
								<Option value="medium">Medium</Option>
								<Option value="heavy">Heavy</Option>
								<Option value="container">Container</Option>
							</Select>
						</Form.Item>

						<Form.Item label="Created Date" name="dateRange">
							<RangePicker
								style={{ width: '100%' }}
								size="large"
								format="DD-MM-YYYY"
								placeholder={['Start Date', 'End Date']}
							/>
						</Form.Item>

						<FilterButtonContainer>
							<ResetButton onClick={handleResetFilters}>Reset</ResetButton>
						</FilterButtonContainer>
					</FilterForm>
				</Box>
			</Drawer>
		</Box>
	);
}
